import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormHelperText,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import axiosInstance from '../AxiosInstance';
import axios from 'axios';
import TimeInput from './TimeInput';
import { getNowInPst } from './submissionUtils';
import { formatLocalYmd } from './formatLocalYmd';

const baseURL = process.env.NODE_ENV === 'production' ? 'https://allcprmanage.com' : 'http://localhost:8080';
const HHMM_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const formatTime = (t) => {
  if (!t) return '00:00';
  const s = String(t).trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s;
};

const formatSlotTime = (start) => {
  if (!start) return '00:00';
  if (typeof start === 'string') return start.slice(11, 16);
  if (start instanceof Date) {
    const h = start.getHours();
    const m = start.getMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return '00:00';
};

const formatSlotDate = (start) => {
  if (!start) return '';
  if (typeof start === 'string') return start.slice(0, 10);
  if (start instanceof Date) return formatLocalYmd(start);
  return '';
};

const parseHHMM = (str) => {
  if (!str || !HHMM_REGEX.test(String(str).trim())) return null;
  const [h, m] = String(str).trim().split(':').map(Number);
  return h * 60 + m;
};

const ProposalSlotDetailDialog = ({
  open,
  onClose,
  slot,
  onSave,
  isManagerMode,
  instructorId,
  managerId,
  token,
}) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');
  const [timeOrderError, setTimeOrderError] = useState('');
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeAction, setScopeAction] = useState(null);
  const [scope, setScope] = useState('this');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmScope, setConfirmScope] = useState('this');
  const [saving, setSaving] = useState(false);
  const [confirmSlotDialogOpen, setConfirmSlotDialogOpen] = useState(false);
  const [rejectScopeDialogOpen, setRejectScopeDialogOpen] = useState(false);
  const [rejectConfirmDialogOpen, setRejectConfirmDialogOpen] = useState(false);
  const [rejectScope, setRejectScope] = useState('this');

  const sourceType = slot?.extendedProps?.sourceType || 'INSTRUCTOR';
  const creatorName = slot?.extendedProps?.creatorName || 'Unknown';
  const creatorLabel = sourceType === 'MANAGER' ? 'Manager' : 'Instructor (self)';
  const endTimeProp = slot?.extendedProps?.endTime ?? (slot?.end ? formatSlotTime(slot.end) : '00:00');
  const rawId = slot?.extendedProps?.rawId;
  const isSchedule = slot?.extendedProps?.isSchedule === true;
  const scheduleId = slot?.extendedProps?.scheduleId;
  const parentId = slot?.extendedProps?.parentId;
  const isRecurringRaw = slot?.extendedProps?.isRecurring;
  const isRecurring = isRecurringRaw === true
    || isRecurringRaw === 1
    || isRecurringRaw === '1'
    || String(isRecurringRaw).toLowerCase() === 'true';
  const rrule = slot?.extendedProps?.rrule;
  const slotDate = slot?.extendedProps?.date || formatSlotDate(slot?.start);

  const frequencyLabel = rrule ? rrule.charAt(0) + rrule.slice(1).toLowerCase() : null;

  const needsScopeChoice = isRecurring && parentId == null;
  const needsRejectScopeChoice = needsScopeChoice && isManagerMode;

  const isOwnManagerSlot = isManagerMode
    && sourceType === 'MANAGER';
  const isOwnSlot =
    isOwnManagerSlot || (!isManagerMode && sourceType === 'INSTRUCTOR');
  const isOtherPartySlot =
    (isManagerMode && sourceType === 'INSTRUCTOR') || (!isManagerMode && sourceType === 'MANAGER');
  const isRejected = slot?.extendedProps?.isRejected === true;
  const parentSeriesIsRecurring = slot?.extendedProps?.parentSeriesIsRecurring === true;
  const parentSeriesRrule = slot?.extendedProps?.parentSeriesRrule;

  // Delete scope: only for real recurring series (not for rejected rows; rejection exceptions
  // have parent_id set but are not a recurring series delete). Exception rows may omit rrule on
  // the client until parentSeries* is filled in ProposalCalendar.
  const isSeriesMember =
    parentId == null
      ? (isRecurring || !!rrule)
      : (isRecurring || !!rrule || parentSeriesIsRecurring || !!parentSeriesRrule);
  const needsDeleteScopeChoice = !isSchedule && !isRejected && isSeriesMember;
  const canEdit = !isSchedule && isOwnSlot && onSave != null;
  const canConfirmRejectOther = isOtherPartySlot && !isSchedule && !isRejected && onSave != null;
  const canManagerConfirmOwn =
    isOwnManagerSlot && !isSchedule && !isRejected && onSave != null;
  const canManagerCancelSchedule =
    isManagerMode && isSchedule && !!scheduleId && onSave != null;

  // Manager can only confirm their own targets at least 7 days from "today" (PST midnight).
  let managerConfirmDisabled = false;
  if (slotDate) {
    const nowPst = getNowInPst();
    const todayPst = new Date(Date.UTC(
      nowPst.getUTCFullYear(),
      nowPst.getUTCMonth(),
      nowPst.getUTCDate(),
      0, 0, 0,
    ));
    const diffMs = new Date(`${slotDate}T00:00:00Z`).getTime() - todayPst.getTime();
    const diffDays = diffMs / (24 * 60 * 60 * 1000);
    if (isManagerMode && sourceType === 'MANAGER') {
      managerConfirmDisabled = diffDays < 7;
    }
  }

  useEffect(() => {
    if (open && slot) {
      setStartTime(formatSlotTime(slot.start));
      setEndTime(formatTime(endTimeProp));
      setStartError('');
      setEndError('');
      setTimeOrderError('');
    }
  }, [open, slot, endTimeProp]);

  const validateStart = (val) => {
    if (!val || !String(val).trim()) return '';
    if (!HHMM_REGEX.test(String(val).trim())) return 'Must be HH:MM format';
    return '';
  };

  const validateEnd = (val) => {
    if (!val || !String(val).trim()) return '';
    if (!HHMM_REGEX.test(String(val).trim())) return 'Must be HH:MM format';
    return '';
  };

  const validateOrder = (s, e) => {
    const sm = parseHHMM(s);
    const em = parseHHMM(e);
    if (sm == null || em == null) return '';
    if (sm >= em) return 'Start time must be before end time';
    return '';
  };

  const handleStartBlur = () => {
    setStartError(validateStart(startTime));
    setTimeOrderError(validateOrder(startTime, endTime));
  };

  const handleEndBlur = () => {
    setEndError(validateEnd(endTime));
    setTimeOrderError(validateOrder(startTime, endTime));
  };

  const handleStartChange = (e) => {
    setStartTime(e.target.value);
    setStartError(validateStart(e.target.value));
    setTimeOrderError(validateOrder(e.target.value, endTime));
  };

  const handleEndChange = (e) => {
    setEndTime(e.target.value);
    setEndError(validateEnd(e.target.value));
    setTimeOrderError(validateOrder(startTime, e.target.value));
  };

  const originalStart = slot ? formatSlotTime(slot.start) : '';
  const originalEnd = slot ? formatTime(endTimeProp) : '';
  const hasChanges = startTime.trim() !== originalStart || endTime.trim() !== originalEnd;
  const startValid = !validateStart(startTime);
  const endValid = !validateEnd(endTime);
  const orderValid = !validateOrder(startTime, endTime);
  const canUpdate = hasChanges && startValid && endValid && orderValid && !saving;

  const buildPayload = (thisEventOnly) => ({
    id: rawId,
    parentId: parentId || null,
    date: slotDate,
    startTime: startTime.trim().length <= 5 ? `${startTime.trim()}:00` : startTime.trim(),
    endTime: endTime.trim().length <= 5 ? `${endTime.trim()}:00` : endTime.trim(),
    thisEventOnly,
  });

  const callUpdate = async (thisEventOnly) => {
    const payload = buildPayload(thisEventOnly);
    setSaving(true);
    try {
      if (isManagerMode) {
        await axiosInstance.put('/proposal', payload, {
          params: { instructorId, managerId },
        });
      } else {
        await axios.put(`${baseURL}/proposal/public`, payload, { params: { token } });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Update failed:', err);
      alert(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const callDelete = async (thisEventOnly) => {
    const payload = {
      id: rawId,
      parentId: parentId || null,
      date: slotDate,
      startTime: slot ? formatSlotTime(slot.start) : '00:00',
      endTime: slot ? formatTime(endTimeProp) : '00:00',
      thisEventOnly,
    };
    setSaving(true);
    try {
      if (isManagerMode) {
        await axiosInstance.delete('/proposal', {
          data: payload,
          params: { instructorId, managerId },
        });
      } else {
        await axios.delete(`${baseURL}/proposal/public`, {
          data: payload,
          params: { token },
        });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Delete failed:', err);
      alert(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const callDeleteSchedule = async () => {
    if (!scheduleId) return;
    setSaving(true);
    try {
      await axiosInstance.delete('/instructor/schedule', { params: { id: String(scheduleId) } });
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Delete schedule failed:', err);
      alert(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClick = () => {
    setScopeAction('update');
    if (needsScopeChoice) {
      setScope('this');
      setScopeDialogOpen(true);
    } else {
      setConfirmAction('update');
      setConfirmScope(parentId != null ? 'this' : 'all');
      setConfirmDialogOpen(true);
    }
  };

  const handleDeleteClick = () => {
    setScopeAction('delete');
    if (needsDeleteScopeChoice) {
      setScope('this');
      setScopeDialogOpen(true);
    } else {
      setConfirmAction('delete');
      setConfirmScope('this');
      setConfirmDialogOpen(true);
    }
  };

  const handleScopeConfirm = () => {
    setScopeDialogOpen(false);
    setConfirmAction(scopeAction);
    setConfirmScope(scope);
    setConfirmDialogOpen(true);
  };

  const handleConfirmConfirm = () => {
    setConfirmDialogOpen(false);
    const thisEventOnly = confirmScope === 'this';
    if (confirmAction === 'update') {
      callUpdate(thisEventOnly);
    } else {
      callDelete(thisEventOnly);
    }
  };

  const buildConfirmPayload = () => ({
    id: rawId,
    parentId: parentId || null,
    date: slotDate,
    startTime: slot ? formatSlotTime(slot.start) : '00:00',
    endTime: slot ? formatTime(endTimeProp) : '00:00',
  });

  const buildRejectPayload = (thisEventOnly) => ({
    id: rawId,
    parentId: parentId || null,
    date: slotDate,
    startTime: slot ? formatSlotTime(slot.start) : '00:00',
    endTime: slot ? formatTime(endTimeProp) : '00:00',
    thisEventOnly,
  });

  const callConfirm = async () => {
    const payload = buildConfirmPayload();
    const st = payload.startTime.trim();
    const et = payload.endTime.trim();
    payload.startTime = st.length <= 5 ? `${st}:00` : st;
    payload.endTime = et.length <= 5 ? `${et}:00` : et;
    setSaving(true);
    try {
      if (isManagerMode) {
        await axiosInstance.post('/proposal/confirm', payload, {
          params: { instructorId, managerId },
        });
      } else {
        await axios.post(`${baseURL}/proposal/confirm/public`, payload, { params: { token } });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Confirm failed:', err);
      alert(err.response?.data?.message || err.message || 'Confirm failed');
    } finally {
      setSaving(false);
    }
  };

  const callReject = async (thisEventOnly) => {
    const payload = buildRejectPayload(thisEventOnly);
    setSaving(true);
    try {
      if (isManagerMode) {
        await axiosInstance.post('/proposal/reject', payload, {
          params: { instructorId, managerId },
        });
      } else {
        await axios.post(`${baseURL}/proposal/reject/public`, payload, { params: { token } });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Reject failed:', err);
      alert(err.response?.data?.message || err.message || 'Reject failed');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmClick = () => {
    setConfirmSlotDialogOpen(true);
  };

  const handleConfirmSlotConfirm = () => {
    setConfirmSlotDialogOpen(false);
    callConfirm();
  };

  const handleRejectClick = () => {
    if (needsRejectScopeChoice) {
      setRejectScope('this');
      setRejectScopeDialogOpen(true);
    } else {
      // Instructor side or non-recurring: always reject this event only
      setRejectScope('this');
      setRejectConfirmDialogOpen(true);
    }
  };

  const handleRejectScopeConfirm = () => {
    setRejectScopeDialogOpen(false);
    setRejectConfirmDialogOpen(true);
  };

  const handleRejectConfirmConfirm = () => {
    setRejectConfirmDialogOpen(false);
    callReject(rejectScope === 'this');
  };

  if (!slot) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Slot Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Created by:</b> {creatorName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Type:</b> {creatorLabel}
          </Typography>
          {isSchedule && (
            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
              <b>Schedule</b>
            </Typography>
          )}
          {isRejected && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              <b>Rejected</b>
            </Typography>
          )}
          {needsScopeChoice && (
            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
              <b>Recurring</b>{frequencyLabel ? ` (${frequencyLabel})` : ''}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <b>Date:</b> {formatSlotDate(slot.start)}
          </Typography>
          {canEdit ? (
            <>
              <TimeInput
                fullWidth
                size="small"
                label="Start Time"
                placeholder="HHMM"
                value={startTime}
                onChange={handleStartChange}
                onBlur={handleStartBlur}
                error={!!startError}
                helperText={startError}
                margin="dense"
              />
              <TimeInput
                fullWidth
                size="small"
                label="End Time"
                placeholder="HHMM"
                value={endTime}
                onChange={handleEndChange}
                onBlur={handleEndBlur}
                error={!!endError}
                helperText={endError}
                margin="dense"
              />
              {timeOrderError && (
                <FormHelperText error sx={{ mt: -1, mb: 1 }}>
                  {timeOrderError}
                </FormHelperText>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Time:</b> {formatSlotTime(slot.start)} - {formatTime(endTimeProp)}
            </Typography>
          )}
          {canManagerConfirmOwn && managerConfirmDisabled && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
              Confirm is disabled: managers can only confirm their own targets when the slot date is on or after{' '}
              <b>today (PST) + 7 days</b>. Past dates and nearer dates cannot be confirmed here. Pick a later day on
              the calendar or use Confirm All for eligible days.
            </Typography>
          )}
          {canEdit && !canUpdate && !hasChanges && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
              Change start or end time to enable Update.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {(canConfirmRejectOther || canManagerConfirmOwn) && (
            <>
              {canConfirmRejectOther && (
                <Button color="error" onClick={handleRejectClick} disabled={saving}>
                  Reject
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmClick}
                disabled={
                  saving ||
                  (isManagerMode && sourceType === 'MANAGER' && managerConfirmDisabled)
                }
                title={
                  isManagerMode && sourceType === 'MANAGER' && managerConfirmDisabled
                    ? 'Slot date must be at least 7 days from today (PST) for manager to confirm own target'
                    : undefined
                }
              >
                Confirm
              </Button>
            </>
          )}
          {canManagerCancelSchedule && (
            <Button
              color="error"
              onClick={callDeleteSchedule}
              disabled={saving}
            >
              Cancel Schedule
            </Button>
          )}
          {canEdit && (
            <>
              <Button color="error" onClick={handleDeleteClick} disabled={saving}>
                Delete
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdateClick}
                disabled={!canUpdate}
              >
                Update
              </Button>
            </>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <MuiDialog open={scopeDialogOpen} onClose={() => setScopeDialogOpen(false)}>
        <MuiDialogTitle>
          {scopeAction === 'update' ? 'Update' : 'Delete'} - Apply to
        </MuiDialogTitle>
        <MuiDialogContent>
          <FormControl component="fieldset">
            <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
              <FormControlLabel value="this" control={<Radio />} label="This event only" />
              <FormControlLabel value="all" control={<Radio />} label="All series" />
            </RadioGroup>
          </FormControl>
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={() => setScopeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleScopeConfirm}>
            Continue
          </Button>
        </MuiDialogActions>
      </MuiDialog>

      <MuiDialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <MuiDialogTitle>Confirm</MuiDialogTitle>
        <MuiDialogContent>
          Are you sure you want to {confirmAction} this {confirmScope === 'this' ? 'event' : 'series'}?
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === 'delete' ? 'error' : 'primary'}
            onClick={handleConfirmConfirm}
            disabled={saving}
          >
            {confirmAction === 'delete' ? 'Delete' : 'Update'}
          </Button>
        </MuiDialogActions>
      </MuiDialog>

      <MuiDialog open={confirmSlotDialogOpen} onClose={() => setConfirmSlotDialogOpen(false)}>
        <MuiDialogTitle>Confirm Slot</MuiDialogTitle>
        <MuiDialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to confirm this slot? It will be added to your schedule.
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
            Once confirmed, this cannot be changed. Please verify before proceeding.
          </Typography>
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={() => setConfirmSlotDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConfirmSlotConfirm} disabled={saving}>
            Confirm
          </Button>
        </MuiDialogActions>
      </MuiDialog>

      <MuiDialog open={rejectScopeDialogOpen} onClose={() => setRejectScopeDialogOpen(false)}>
        <MuiDialogTitle>Reject - Apply to</MuiDialogTitle>
        <MuiDialogContent>
          <FormControl component="fieldset">
            <RadioGroup value={rejectScope} onChange={(e) => setRejectScope(e.target.value)}>
              <FormControlLabel value="this" control={<Radio />} label="This event only" />
              <FormControlLabel value="all" control={<Radio />} label="All series" />
            </RadioGroup>
          </FormControl>
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={() => setRejectScopeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectScopeConfirm}>
            Continue
          </Button>
        </MuiDialogActions>
      </MuiDialog>

      <MuiDialog open={rejectConfirmDialogOpen} onClose={() => setRejectConfirmDialogOpen(false)}>
        <MuiDialogTitle>Confirm Reject</MuiDialogTitle>
        <MuiDialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to reject this {rejectScope === 'this' ? 'event' : 'series'}?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
            Once rejected, this cannot be undone. Please confirm before proceeding.
          </Typography>
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={() => setRejectConfirmDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectConfirmConfirm} disabled={saving}>
            Reject
          </Button>
        </MuiDialogActions>
      </MuiDialog>
    </>
  );
};

export default ProposalSlotDetailDialog;
